import { CircularProgress, Scrollbar, Button } from "@ann/web-components";
import { DrivesContentWrapper, DrivesContentWrapperLoading, DrivesContentWrapperScrollbar } from "./DrivesView.styled";
import { useState, useEffect, useMemo, useCallback, useDeferredValue } from "react";
import { useOutletContext } from "react-router-dom";
import { useStore } from "../../../../../../../hooks";
import { observer } from "mobx-react";
import { useParams } from "react-router-dom";
import AccessOptionsHeader from "./components/AccessOptionsHeader";
import DrivesDocumentsList from "./components/DrivesDocumentsList";
import { ModalBase } from "../../../../components/Modal";
import ItemsToRemove from "./components/itemsToRemove";


// Track renders
let renderCount = 0;

const DrivesContent = observer(() => {
    renderCount++;
    console.time(`⏱️ DrivesView Render #${renderCount}`);
    console.log(`🏁 DrivesView: RENDER #${renderCount} started`);
    
    const { viewMode, setShowSlideoutDriveDetail, setShowSlideoutDocumentDetail, setSelectedDocument, searchTerm } = useOutletContext()
    console.log(`📥 DrivesView Render #${renderCount}: Got outlet context`);
    
    const { knowledgeStore } = useStore();
    console.log(`📊 DrivesView Render #${renderCount}: Got stores`);
    
    const [selectMode, setSelectMode] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const { agentId } = useParams();
    
    // ✅ PERFORMANCE: Start with 0 items, then progressively load
    const [displayedCount, setDisplayedCount] = useState(0);
    const [isInitialLoad, setIsInitialLoad] = useState(true);

    // Debug: Log component lifecycle
    useEffect(() => {
        const mountTime = performance.now();
        console.log('🎬 DrivesView: Component mounted');
        return () => {
            const unmountTime = performance.now();
            const totalMountDuration = unmountTime - mountTime;
            console.log(`🔚 DrivesView: Component unmounted (was mounted for ${totalMountDuration.toFixed(2)}ms)`);
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (agentId && agentId !== knowledgeStore.agentStore.lastLoadedAgentId) {
                console.time('⏱️ DrivesView Data Fetch');
                console.log('📥 DrivesView: Fetching drives & documents for agent:', agentId);
                try {
                    await knowledgeStore.agentStore.fetchDocumentsDrivesData(agentId);
                    console.timeEnd('⏱️ DrivesView Data Fetch');
                    console.log('✅ DrivesView: Data loaded and cached');
                } catch (error) {
                    console.timeEnd('⏱️ DrivesView Data Fetch');
                    console.error('❌ DrivesView: Error loading data:', error);
                }
            } else {
                console.log('✅ DrivesView: Using cached data for agent:', agentId, '- No fetch needed');
                console.log('📊 Cached drives:', knowledgeStore.agentStore.drives.length);
                console.log('📊 Cached documents:', knowledgeStore.agentStore.documents.length);
            }
        };

        loadData();
    }, [agentId, knowledgeStore.agentStore.lastLoadedAgentId]);

    // ✅ PERFORMANCE: Memoized callbacks
    const removeAccessToDocument = useCallback((uuid) => {
        knowledgeStore.agentStore.unassignAgentFromDocument(uuid, agentId);
    }, [knowledgeStore.agentStore, agentId]);

    const handleSelectDrive = useCallback((drive) => {
        knowledgeStore.toggleDriveSelection(drive);
    }, [knowledgeStore]);

    const handleSelectDocument = useCallback((document) => {
        knowledgeStore.toggleDocumentSelection(document);
    }, [knowledgeStore]);

    const clearSelections = useCallback(() => {
        knowledgeStore.clearDriveSelections();
        knowledgeStore.clearDocumentSelections();
    }, [knowledgeStore]);

    const onClose = () => {
        setIsOpen(false);
        knowledgeStore.clearDriveSelections();
        knowledgeStore.clearDocumentSelections();
        setSelectMode(false);
    }
    const onRemoveAccess = async () => {
        setIsOpen(false);
        await knowledgeStore.onRemoveAccess(agentId);
        setSelectMode(false);
    }


    const isLoading = knowledgeStore.agentStore.documentsLoading || knowledgeStore.agentStore.drivesLoading;

    // ✅ PERFORMANCE: Defer filtering to prevent blocking UI on tab switch
    const deferredSearchTerm = useDeferredValue(searchTerm);

    // Función para filtrar drives y documentos basándose en el término de búsqueda
    const filteredDrives = useMemo(() => {
        console.time('⏱️ Filter Drives');
        console.log('🔍 Filtering drives, total:', knowledgeStore.agentStore.drives.length);
        
        let result;
        if (!deferredSearchTerm || deferredSearchTerm.trim() === '') {
            result = knowledgeStore.agentStore.drives;
        } else {
            const searchLower = deferredSearchTerm.toLowerCase();
            result = knowledgeStore.agentStore.drives.filter(drive =>
                drive.name?.toLowerCase().includes(searchLower) ||
                drive.description?.toLowerCase().includes(searchLower) ||
                drive.vaultUuid?.toLowerCase().includes(searchLower)
            );
        }
        
        console.timeEnd('⏱️ Filter Drives');
        console.log('✅ Filtered drives:', result.length);
        return result;
    }, [knowledgeStore.agentStore.drives, deferredSearchTerm]);

    const filteredDocuments = useMemo(() => {
        console.time('⏱️ Filter Documents');
        console.log('🔍 Filtering documents, total:', knowledgeStore.agentStore.documents.length);
        
        let result;
        if (!deferredSearchTerm || deferredSearchTerm.trim() === '') {
            result = knowledgeStore.agentStore.documents;
        } else {
            const searchLower = deferredSearchTerm.toLowerCase();
            result = knowledgeStore.agentStore.documents.filter(document =>
                document.name?.toLowerCase().includes(searchLower) ||
                document.type?.toLowerCase().includes(searchLower) ||
                document.uuid?.toLowerCase().includes(searchLower) ||
                document.metadata?.source?.toLowerCase().includes(searchLower)
            );
        }
        
        console.timeEnd('⏱️ Filter Documents');
        console.log('✅ Filtered documents:', result.length);
        return result;
    }, [knowledgeStore.agentStore.documents, deferredSearchTerm]);

    const totalSources = filteredDrives.length + filteredDocuments.length;

    // ✅ PERFORMANCE: Progressive rendering - only show subset initially for fast tab switching
    const combinedItems = useMemo(() => {
        console.time('⏱️ Combine Items');
        const combined = [...filteredDrives, ...filteredDocuments];
        console.timeEnd('⏱️ Combine Items');
        console.log('📦 Combined items:', combined.length);
        return combined;
    }, [filteredDrives, filteredDocuments]);

    const displayedItems = useMemo(() => {
        console.time('⏱️ Slice Displayed Items');
        const sliced = combinedItems.slice(0, displayedCount);
        console.timeEnd('⏱️ Slice Displayed Items');
        console.log('👁️ Displaying:', sliced.length, 'of', combinedItems.length, 'items');
        return sliced;
    }, [combinedItems, displayedCount]);

    const displayedDrives = useMemo(() => {
        console.time('⏱️ Filter Displayed Drives');
        const drives = displayedItems.filter(item => item.vaultUuid);
        console.timeEnd('⏱️ Filter Displayed Drives');
        console.log('🗂️ Displayed drives:', drives.length);
        return drives;
    }, [displayedItems]);

    const displayedDocuments = useMemo(() => {
        console.time('⏱️ Filter Displayed Documents');
        const docs = displayedItems.filter(item => item.uuid && !item.vaultUuid);
        console.timeEnd('⏱️ Filter Displayed Documents');
        console.log('📄 Displayed documents:', docs.length);
        return docs;
    }, [displayedItems]);

    const hasMore = combinedItems.length > displayedCount;

    const handleLoadMore = useCallback(() => {
        setDisplayedCount(prev => Math.min(prev + 30, combinedItems.length));
    }, [combinedItems.length]);

    // ✅ PERFORMANCE: Load immediately after mount using requestAnimationFrame
    useEffect(() => {
        if (isInitialLoad && !isLoading) {
            console.log('🚀 DrivesView: Starting immediate load...');
            
            // Use requestAnimationFrame for immediate but non-blocking load
            requestAnimationFrame(() => {
                console.log('📦 DrivesView: Loading 15 items...');
                setDisplayedCount(15);
                setIsInitialLoad(false);
            });
        }
    }, [isInitialLoad, isLoading]);

    // Reset displayed count when search changes
    useEffect(() => {
        setDisplayedCount(10);
    }, [deferredSearchTerm]);

    // ✅ PERFORMANCE: Memoized selected items to avoid O(n²) operations
    const selectedDrivesFiltered = useMemo(() => {
        return knowledgeStore.agentStore.drives.filter(drive =>
            knowledgeStore.selectedDrivesSet.has(drive.vaultUuid)  // O(1) lookup instead of O(n)!
        ) || [];
    }, [knowledgeStore.agentStore.drives, knowledgeStore.selectedDrivesSet]);

    const selectedDocumentsFiltered = useMemo(() => {
        return knowledgeStore.agentStore.documents.filter(document =>
            knowledgeStore.selectedDocumentsSet.has(document.uuid)  // O(1) lookup instead of O(n)!
        ) || [];
    }, [knowledgeStore.agentStore.documents, knowledgeStore.selectedDocumentsSet]);

    // ✅ PERFORMANCE: Memoized combined selected items array
    const allSelectedItems = useMemo(() => {
        return [...knowledgeStore.selectedDrives, ...knowledgeStore.selectedDocuments];
    }, [knowledgeStore.selectedDrives, knowledgeStore.selectedDocuments]);

    // Debug: Log when render completes (paint/commit phase)
    useEffect(() => {
        console.log(`🎨 DrivesView Render #${renderCount}: Paint/Commit completed`);
    });

    console.timeEnd(`⏱️ DrivesView Render #${renderCount}`);
    console.log(`✅ DrivesView Render #${renderCount}: Component body execution completed, starting JSX return`);

    if (isLoading) {
        console.log('⏳ DrivesView: Showing loading spinner - fetching data');
        return (
            <DrivesContentWrapperLoading>
                <CircularProgress size="xl" />
            </DrivesContentWrapperLoading>
        );
    }

    // ✅ PERFORMANCE: Show loading state while items are being loaded
    if (isInitialLoad && displayedCount === 0) {
        console.log('⏳ DrivesView: Initial load - showing placeholder');
        return (
            <DrivesContentWrapperLoading>
                <CircularProgress size="xl" />
            </DrivesContentWrapperLoading>
        );
    }

    console.log(`🖼️ DrivesView Render #${renderCount}: Returning JSX with`, displayedDrives.length, 'drives and', displayedDocuments.length, 'documents');

    return (
        <>

            <ModalBase
                isOpen={isOpen}
                onClose={onClose}
                title="Are you sure you want to remove access?"
                description="Your agent won't be able to use it for responses until you reconnect it."
                buttonTitle="Remove access"
                onClick={onRemoveAccess}
            >
                <ItemsToRemove
                    selectedDrives={selectedDrivesFiltered}
                    selectedDocuments={selectedDocumentsFiltered}
                />
            </ModalBase>

            <AccessOptionsHeader
                clearSelections={clearSelections}
                selectMode={selectMode}
                setSelectMode={setSelectMode}
                selectedItems={allSelectedItems}
                setIsOpen={setIsOpen}
                totalSources={totalSources}
                totalSelected={knowledgeStore.selectedDrivesCount + knowledgeStore.selectedDocumentsCount}

            />

            <DrivesContentWrapperScrollbar>
                <Scrollbar size="small">
                    <DrivesContentWrapper>
                        <DrivesDocumentsList
                            drives={displayedDrives}
                            documents={displayedDocuments}
                            selectMode={selectMode}
                            setShowSlideoutDriveDetail={setShowSlideoutDriveDetail}
                            setShowSlideoutDocumentDetail={setShowSlideoutDocumentDetail}
                            setSelectedDocument={setSelectedDocument}
                            viewMode={viewMode}
                            removeAccessToDocument={removeAccessToDocument}
                            selectedDrives={knowledgeStore.selectedDrives}
                            selectedDocuments={knowledgeStore.selectedDocuments}
                            handleSelectDrive={handleSelectDrive}
                            handleSelectDocument={handleSelectDocument}
                            removeAccessLoading={knowledgeStore.agentStore.unassignAgentFromDriveLoading || knowledgeStore.agentStore.unassignAgentFromDocumentLoading}
                        />
                        
                        {/* ✅ PERFORMANCE: Load More button for progressive rendering */}
                        {hasMore && (
                            <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', padding: '20px', width: '100%' }}>
                                <Button
                                    label={`Load More (${combinedItems.length - displayedCount} remaining)`}
                                    onClick={handleLoadMore}
                                    size="medium"
                                    type="secondary"
                                />
                            </div>
                        )}

                    </DrivesContentWrapper >
                </Scrollbar>
            </DrivesContentWrapperScrollbar>

        </>
    )
})

export default DrivesContent;
